import jsPDF from 'jspdf';
import 'jspdf-autotable';
import config from '../Common_Files/Config';
import axios from 'axios';

const generateUniqueFilename = (baseName, extension) => {
  const timestamp = new Date().toISOString().replace(/[-:.]/g, "");
  return `${baseName}_${timestamp}.${extension}`;
};

const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (e) {
    return dateString;
  }
};

const formatDateTimeForDisplay = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }) + ' ' + date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return dateString;
  }
};

const generatePurchaseInvoicePDF = async (id, viewMode = false, documentType = 'Collection Note') => {
  const accessToken = localStorage.getItem('accessToken');
  const formData = new FormData();
  formData.set('id', id);
  const apiUrl = `${config.api_url}/ws-get-current-purchase-order-invoice`;

  try {
    const response = await axios.post(apiUrl, formData, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });
    const data = await response.data;
    const doc = new jsPDF('portrait', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    let y = margin;

    const wrapText = (text, maxWidth) => doc.splitTextToSize(text || '', maxWidth);

    const loadImageAsDataUrl = async (src) => {
      try {
        const res = await fetch(src, { cache: 'no-store' });
        const blob = await res.blob();
        return await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch {
        return null;
      }
    };

    const logoDataUrl = await loadImageAsDataUrl('/assets/img/logo.jpg');
    const logoWidth = 28;
    const logoHeight = 12;
    if (logoDataUrl) {
      doc.addImage(logoDataUrl, 'JPEG', margin, y, logoWidth, logoHeight);
    }

    const leftX = margin + logoWidth + 5;
    doc.setFontSize(12).setFont('helvetica', 'bold');
    doc.text('Star Pacific UK LTD', leftX, y + 4);

    const companyAddress = [
      'Unit 6 Abenglen Industrial Estate,',
      'Betam Road, Hayes',
      'Middlesex, London UB3 1AT',
      'United Kingdom'
    ];
    let smallLineHeight = 4;
    doc.setFontSize(8).setFont('helvetica', 'normal');
    companyAddress.forEach((line, idx) => {
      doc.text(line, leftX, y + 4 + smallLineHeight * (idx + 1));
    });

    const header = data.headerSection2 || {};
    const rightX = pageWidth - margin;
    doc.setFontSize(16).setFont('helvetica', 'bold');
    doc.setTextColor(255, 99, 71);
    const displayTitle = (documentType === 'P.O Invoice' || documentType === 'Purchase Order') ? 'Purchase Order' : documentType;
    doc.text(displayTitle, rightX, y - 1, { align: 'right' });
    doc.setTextColor(0, 0, 0);

    const regInfoStartX = leftX + 50;
    doc.setFontSize(8).setFont('helvetica', 'bold');
    doc.text('Co. Reg. No.: 7298396', regInfoStartX, y + 4);
    doc.text('VAT Reg. No.: GB994533184', regInfoStartX, y + 4 + smallLineHeight);
    doc.text('Tel: 44-208 329 3800', regInfoStartX, y + 4 + smallLineHeight * 2);

    if (documentType === 'P.O Invoice' || documentType === 'Purchase Order') 
      {
      const supplierTitle = 'Supplier Address:';
      const supplierLines = [
        header.supplier_name || '',
        header.supplier_address_line1 || '',
        header.supplier_address_line2 || '',
        header.city || '',
        header.postcode || '',
        header.country || ''
      ].filter(Boolean);

      let addrStartY = y + 4 + (smallLineHeight * (companyAddress.length + 1));
      doc.setFontSize(8).setFont('helvetica', 'bold');
      doc.text(supplierTitle, leftX - 25, addrStartY);
      const valueStartX = leftX - 25 + doc.getTextWidth(supplierTitle + ' ');
      doc.setFontSize(8).setFont('helvetica', 'normal');
      
      let supplierY = addrStartY;
      let totalSupplierLines = 0;
      if (supplierLines.length > 0) {
        supplierLines.forEach((line) => {
          const wrappedLines = wrapText(line, 60);
          wrappedLines.forEach((wrappedLine) => {
            doc.text(wrappedLine, valueStartX, supplierY);
            supplierY += smallLineHeight;
            totalSupplierLines++;
          });
        });
      }

      const purchaseInfoData = [
        { label: 'Currency', value: header.currency_id || 'GBP' },
        { label: 'PO No', value: header.reference_no || '' },
        { label: 'Job Ref', value: header.job_ref || '' },
        { label: 'Date', value: formatDateForDisplay(header.arrival_date) || '' }
      ];

      let suppStartY = y + 4 + (smallLineHeight * 4);
      doc.setFontSize(8).setFont('helvetica', 'normal');
      let currentY = suppStartY;
      const rightSectionMaxWidth = 80;
      
      purchaseInfoData.forEach((item) => {
        if (item.value) {
          const fullText = `${item.label}: ${item.value}`;
          const wrappedLines = wrapText(fullText, rightSectionMaxWidth);
          wrappedLines.forEach((line) => {
            doc.text(line, rightX - 40, currentY, { align: 'left' });
            currentY += smallLineHeight;
          });
        }
      });

      const supplierLinesCount = totalSupplierLines;
      y = Math.max(addrStartY + (supplierLinesCount * smallLineHeight), currentY) + 6;

      const supplierInfoY = y + 10;
      const sectionWidth = (pageWidth - margin * 2) / 3;

      // const vatRepAcc = [
      //   `VAT Reg. No: ${header.vat_id || ''}`,
      //   `Rep: ${header.contact_person_name || ''}`,
      //   `Account No: ${header.bank_account_number || ''}`
      // ].filter(Boolean);
      // vatRepAcc.forEach((line, idx) => doc.text(line, margin, supplierInfoY + idx * 4));

      const poDateJobData = [
        { label: 'PO No', value: header.reference_no || '' },
        { label: 'Date of Creation', value: formatDateTimeForDisplay(header.raised_on) || '' },
        { label: 'Job Ref', value: header.job_ref || '' }
      ];
      
      let middleSectionY = supplierInfoY;
      // PO No / Date / Job Ref — same horizontal start as supplier address lines (valueStartX)
      const deliveryColumnStart = margin + sectionWidth * 2;
      const poJobSectionMaxWidth = Math.max(
        40,
        deliveryColumnStart - valueStartX - 5
      );

      poDateJobData.forEach((item) => {
        if (item.value) {
          const fullText = `${item.label}: ${item.value}`;
          const wrappedLines = wrapText(fullText, poJobSectionMaxWidth);
          wrappedLines.forEach((line) => {
            doc.text(line, valueStartX, middleSectionY);
            middleSectionY += 4;
          });
        }
      });

      let addressSection = [];
      let addressTitle = '';
      if (header.is_wharehouse_or_customer === 'warehouse' && header.delivery_type === 'delivery') {
        addressTitle = 'Delivery Address:';
        const warehouseDisplay = header.warehouse_name
          ? `${header.warehouse_name} (${header.job_ref || ''})`
          : `Warehouse (${header.job_ref || ''})`;
        addressSection = [
          warehouseDisplay,
          header.address_line1 || '',
          header.whcity || header.city || '',
          header.whpostcode || header.postcode || '',
          header.whcountry || header.country || ''
        ].filter(Boolean);
      } else if (header.is_wharehouse_or_customer === 'customer' && header.delivery_type === 'delivery') {
        addressTitle = 'Delivery Address:';
        addressSection = [
          header.name || '',
          header.address_line1 || '',
          header.address_line2 || '',
          header.whcity || header.city || '',
          header.whstate || header.state || '',
          header.whcountry || header.country || '',
          header.whpostcode || header.postcode || ''
        ].filter(Boolean);
      } else if (header.delivery_type === 'collection') {
        addressTitle = 'Collection Address:';
        addressSection = [
          header.address_line1 || '',
          header.address_line2 || '',
          header.whcity || header.city || '',
          header.whpostcode || header.postcode || '',
          header.whcountry || header.country || ''
        ].filter(Boolean);
      }

      let deliveryY = supplierInfoY;
      let totalAddressLines = 0;
      if (addressSection.length > 0) {
        doc.setFontSize(8).setFont('helvetica', 'bold');
        doc.text(addressTitle, margin + sectionWidth * 2, deliveryY);
        deliveryY += 4;
        totalAddressLines++;
        
        doc.setFontSize(8).setFont('helvetica', 'normal');
        addressSection.forEach((line) => {
          const wrappedLines = wrapText(line, 60);
          wrappedLines.forEach((wrappedLine) => {
            doc.text(wrappedLine, margin + sectionWidth * 2, deliveryY);
            deliveryY += 4;
            totalAddressLines++;
          });
        });
      }

      // Left column VAT/Rep/Account block commented out — no lines to reserve
      const vatRepAccLines = 0;
      const middleSectionLines = Math.ceil((middleSectionY - supplierInfoY) / 4);
      const maxSectionLines = Math.max(vatRepAccLines, middleSectionLines, totalAddressLines);
      y = supplierInfoY + maxSectionLines * 4 + 10;

      const columns = [
        'Line No', 'SKU Code', 'Product Description', 'Pack', 'Size', 'Unit Barcode', 'Case Qty', 'Unit Price', 'Amount', 'Vat Rate (%)', 'Vat Amount', 'Total Amount'
      ];

      let totalVatAmount = 0;
      let totalAmount = 0;
      let totalCaseQty = 0;

      const tableBody = data.products.map((item, index) => {
        const quantity = parseFloat(item.qty || 0);
        const unitPrice = parseFloat(item.cost || item.price || 0);
        const netPrice = quantity * unitPrice;
        const vatRate = parseFloat(item.rate || item.vat || 0);
        const vatAmount = netPrice * (vatRate / 100);
        const totalPrice = netPrice + vatAmount;

        totalVatAmount += vatAmount;
        totalAmount += totalPrice;
        totalCaseQty += quantity;

        return [
          index + 1,
          item.code || '',
          wrapText(item.product_desc || '', 50).join('\n'),
          item.pack || '',
          item.size || '',
          item.unit_barcode || '',
          quantity.toFixed(0),
          unitPrice.toFixed(2),
          netPrice.toFixed(2),
          vatRate.toFixed(2),
          vatAmount.toFixed(2),
          totalPrice.toFixed(2)
        ];
      });

      tableBody.push(['', '', 'Total:', '', '', '', totalCaseQty.toFixed(0), '', '', '', totalVatAmount.toFixed(2), totalAmount.toFixed(2)]);

      doc.autoTable({
        startY: y,
        head: [columns],
        body: tableBody,
        theme: 'grid',
        headStyles: { fillColor: '#f8f9fa', textColor: '#404040', fontSize: 8, lineWidth: 0.09, lineColor: '#000' },
        bodyStyles: { fontSize: 7, lineHeight: 1.5, lineColor: '#000', lineWidth: 0.09 },
        margin: { top: 10 }
      });

      y = doc.autoTable.previous.finalY + 15;

      const gross_weight = data.products.reduce((acc, item) => acc + Number(item.gross_weight || 0), 0);
      const cbm = data.products.reduce((acc, item) => acc + Number(item.cbm || 0), 0);
      const net_weight = data.products.reduce((acc, item) => acc + Number(item.net_weight || 0), 0);

      const footerColumns = [
        [
          { text: `Notes: ${header.delivery_notes || ''}`, size: 8, bold: false },
          { text: `Ref: ${header.job_ref || ''}`, size: 8, bold: false }
        ],
        [{ text: '', size: 8, bold: false }],
        [
          { text: `Gross Weight: ${gross_weight.toFixed(2)} KGS`, size: 8, bold: false },
          { text: `CBM: ${cbm.toFixed(2)} M3`, size: 8, bold: false },
          { text: `Net Weight: ${net_weight.toFixed(2)} KGS`, size: 8, bold: false }
        ]
      ];

      const measureColumnsHeight = (columnsArr) => {
        const adjustedWidth = (pageWidth - margin * 2) / columnsArr.length;
        let maxH = 0;
        columnsArr.forEach((col) => {
          let h = 0;
          col.forEach((item) => {
            const lines = wrapText(item.text, adjustedWidth - 2);
            h += lines.length * 4.5;
          });
          if (h > maxH) maxH = h;
        });
        return maxH;
      };

      const renderColumns = (columnsArr) => {
        const adjustedWidth = (pageWidth - margin * 2) / columnsArr.length;
        let maxColBottom = y;
        columnsArr.forEach((col, idx) => {
          const colStartX = margin + idx * adjustedWidth;
          let colY = y;
          col.forEach((item) => {
            const wrapped = wrapText(item.text, adjustedWidth - 2);
            doc.setFontSize(item.size - 0.5);
            doc.setFont(item.bold ? 'helvetica' : 'helvetica', item.bold ? 'bold' : 'normal');
            wrapped.forEach((line) => {
              doc.text(line, colStartX, colY);
              colY += 4.5;
            });
          });
          if (colY > maxColBottom) maxColBottom = colY;
        });
        y = maxColBottom + 12;
      };

      const reservedBottom = 28;
      const needed = measureColumnsHeight(footerColumns);
      if (y + needed > pageHeight - margin - reservedBottom) {
        doc.addPage();
        y = margin;
      }
      renderColumns(footerColumns);

    } else {
      let addressTitle = '';
      let addressLines = [];
      if (header.delivery_type === 'warehouse') {
        addressTitle = 'Invoice Address:';
        addressLines = [
          header.address_line1 || '',
          header.address_line2 || '',
          header.city || '',
          header.state || '',
          header.postcode || '',
          header.country || ''
        ].filter(Boolean);
      } else if (header.delivery_type === 'collection') {
        addressTitle = 'Collection Address:';
        addressLines = [
          header.address_line1 || '',
          header.address_line2 || '',
          header.city || '',
          header.state || '',
          header.postcode || '',
          header.country || ''
        ].filter(Boolean);
      } else {
        addressTitle = 'Customer Address:';
        addressLines = [
          header.address_line1 || '',
          header.address_line2 || '',
          header.city || '',
          header.state || '',
          header.postcode || '',
          header.country || ''
        ].filter(Boolean);
      }

      let addrStartY = y + 4 + (smallLineHeight * (companyAddress.length + 1));
      doc.setFontSize(8).setFont('helvetica', 'bold');
      doc.text(addressTitle, leftX - 25, addrStartY);
      const addressValueStartX = leftX - 25 + doc.getTextWidth(addressTitle + ' ');
      doc.setFontSize(8).setFont('helvetica', 'normal');
      let addressY = addrStartY;
      let totalAddressLines = 0;
      if (addressLines.length > 0) {
        addressLines.forEach((line) => {
          const wrappedLines = wrapText(line, 60);
          wrappedLines.forEach((wrappedLine) => {
            doc.text(wrappedLine, addressValueStartX, addressY);
            addressY += smallLineHeight;
            totalAddressLines++;
          });
        });
      }

      const supplierInfo = [
        `Supplier PO: ${header.supplier_po || header.reference_no || ''}`,
        `Collection Date: ${header.collection_date || header.arrival_date || ''}`
      ];

      const previewNote = `Collection Note: ${header.delivery_notes || ''}`;
      const previewMaxWidth = 34;
      const previewMaxLines = 100;
      let previewLines = wrapText(previewNote, previewMaxWidth);
      if (previewLines.length > previewMaxLines) {
        previewLines = previewLines.slice(0, previewMaxLines);
        previewLines.push('...');
      }

      let suppStartY = y + 4 + (smallLineHeight * 4);
      doc.setFontSize(8).setFont('helvetica', 'normal');
      supplierInfo.forEach((line, idx) => {
        doc.text(line, rightX - 30, suppStartY + idx * smallLineHeight, { align: 'left' });
      });
      let collectionNoteY = suppStartY + supplierInfo.length * smallLineHeight;
      previewLines.forEach((line, lineIdx) => {
        doc.text(line, rightX - 30, collectionNoteY + lineIdx * smallLineHeight, { align: 'left' });
      });

      const addressLinesCount = totalAddressLines;
      const totalRightInfoHeight = (supplierInfo.length + previewLines.length) * smallLineHeight;
      y = Math.max(addrStartY + (addressLinesCount * smallLineHeight), suppStartY + totalRightInfoHeight) + 6;

      const columns = [
        'Line No', 'SKU Code', 'Product Description', 'Pack', 'Size', 'Unit Barcode', 'Case Qty', 'Pallet Qty'
      ];

      const tableBody = data.products.map((item, index) => {
        const quantity = parseInt(item.qty || 0, 10);
        const basePalletQty = parseFloat(item.base_pallet_qty || 0);
        const calculatedPalletQty = (basePalletQty > 0) 
          ? Math.ceil(quantity / basePalletQty) 
          : parseInt(item.pallet_qty || 0, 10);

        return [
          index + 1,
          item.code,
          wrapText(item.product_desc, 50).join('\n'),
          item.pack,
          item.size,
          item.unit_barcode,
          quantity,
          calculatedPalletQty
        ];
      });

      const totalQty = data.products.reduce((acc, item) => acc + parseInt(item.qty || 0, 10), 0);
      const totalPalletQty = data.products.reduce((acc, item) => {
        const quantity = parseInt(item.qty || 0, 10);
        const basePalletQty = parseFloat(item.base_pallet_qty || 0);
        const calculatedPalletQty = (basePalletQty > 0) 
          ? Math.ceil(quantity / basePalletQty) 
          : parseInt(item.pallet_qty || 0, 10);
        return acc + calculatedPalletQty;
      }, 0);

      tableBody.push(['', '', '', '', '', 'Total:', totalQty.toString(), totalPalletQty.toString()]);

      doc.autoTable({
        startY: y,
        head: [columns],
        body: tableBody,
        theme: 'grid',
        headStyles: { fillColor: '#f8f9fa', textColor: '#404040', fontSize: 8, lineWidth: 0.09, lineColor: '#000' },
        bodyStyles: { fontSize: 7, lineHeight: 1.5, lineColor: '#000', lineWidth: 0.09 },
        margin: { top: 10 }
      });

      y = doc.autoTable.previous.finalY + 12;

      const gross_weight = data.products.reduce((acc, item) => acc + Number(item.gross_weight || 0), 0);
      const cbm = data.products.reduce((acc, item) => acc + Number(item.cbm || 0), 0);
      const net_weight = data.products.reduce((acc, item) => acc + Number(item.net_weight || 0), 0);

      const footerColumns = [
        [
          { text: `Notes: ${header.delivery_notes || ''}`, size: 8, bold: false },
          { text: `Ref: ${header.job_ref || ''}`, size: 8, bold: false },
        ],
        [{ text: '', size: 8, bold: false }],
        [
          { text: `Gross Weight: ${gross_weight.toFixed(2)} KGS`, size: 8, bold: false },
          { text: `CBM: ${cbm.toFixed(2)} M3`, size: 8, bold: false },
          { text: `Net Weight: ${net_weight.toFixed(2)} KGS`, size: 8, bold: false },
        ]
      ];

      const measureColumnsHeight = (columnsArr) => {
        const adjustedWidth = (pageWidth - margin * 2) / columnsArr.length;
        let maxH = 0;
        columnsArr.forEach((col) => {
          let h = 0;
          col.forEach((item) => {
            const lines = wrapText(item.text, adjustedWidth - 2);
            h += lines.length * 4.5;
          });
          if (h > maxH) maxH = h;
        });
        return maxH;
      };

      const renderColumns = (columnsArr) => {
        const adjustedWidth = (pageWidth - margin * 2) / columnsArr.length;
        let maxColBottom = y;
        columnsArr.forEach((col, idx) => {
          const colStartX = margin + idx * adjustedWidth;
          let colY = y;
          col.forEach((item) => {
            const wrapped = wrapText(item.text, adjustedWidth - 2);
            doc.setFontSize(item.size - 0.5);
            doc.setFont(item.bold ? 'helvetica' : 'helvetica', item.bold ? 'bold' : 'normal');
            wrapped.forEach((line) => {
              doc.text(line, colStartX, colY);
              colY += 4.5;
            });
          });
          if (colY > maxColBottom) maxColBottom = colY;
        });
        y = maxColBottom + 12;
      };

      const palletData = [
        ['CHEP Standard', totalPalletQty.toFixed(0)],
        ['LPR Standard', ''],
        ['IPP Standard', ''],
        ['CHEP Euro', ''],
        ['Others', '']
      ];

      const palletTableWidth = 50;
      doc.autoTable({
        startY: y,
        body: palletData,
        theme: 'grid',
        headStyles: { fillColor: '#f8f9fa', textColor: '#404040', fontSize: 8, lineWidth: 0.09, lineColor: '#000' },
        bodyStyles: { fontSize: 8, lineColor: '#000', lineWidth: 0.09 },
        margin: { left: margin, right: pageWidth - margin - palletTableWidth },
        tableWidth: palletTableWidth,
        columnStyles: { 0: { cellWidth: 35 }, 1: { cellWidth: 15 } }
      });

      const palletTableStartY = y;
      const palletTableEndY = doc.autoTable.previous.finalY;
      
      const signatureBlockStartX = margin + palletTableWidth + 10;
      const driverBlockWidth = 70;
      const warehouseBlockStartX = signatureBlockStartX + driverBlockWidth + 5;
      
      let signatureY = palletTableStartY + 5;

      doc.setFontSize(8).setFont('helvetica', 'bold');
      doc.text('Driver', signatureBlockStartX, signatureY);
      doc.setFontSize(8).setFont('helvetica', 'normal');
      doc.text('Loaded in good condition and correct pallet count', signatureBlockStartX, signatureY + 5);
      doc.text('Name -------------------------', signatureBlockStartX, signatureY + 15);
      doc.text('Sign -------------------------', signatureBlockStartX, signatureY + 25);

      doc.setFontSize(8).setFont('helvetica', 'bold');
      doc.text('Warehouse', warehouseBlockStartX, signatureY);
      doc.setFontSize(8).setFont('helvetica', 'normal');
      doc.text('Visual inspection of pallets', warehouseBlockStartX, signatureY + 5);
      doc.text('and product carried out', warehouseBlockStartX, signatureY + 10);
      doc.text('Name -------------------', warehouseBlockStartX, signatureY + 15);
      doc.text('Sign -------------------', warehouseBlockStartX, signatureY + 25);

      const signatureBlockEndY = signatureY + 30;
      const combinedEndY = Math.max(palletTableEndY, signatureBlockEndY);
      y = combinedEndY + 10;

      const reservedBottom = 28;
      const needed = measureColumnsHeight(footerColumns);
      if (y + needed > pageHeight - margin - reservedBottom) {
        doc.addPage();
        y = margin;
      }
      renderColumns(footerColumns);
    }

    const footerSections3 = [
      [
        { text: 'Subject to Star Pacific terms and conditions of sale, a copy of which is available on request', size: 8, bold: false },
        { text: 'Star Pacific (No. 7298396)', size: 8, bold: false },
        { text: 'Reservation of Title. The property in and the title to all goods sold by Star Pacific shall not pass to the buyer until all monies due and owing have been paid in full', size: 8, bold: false },
      ]
    ];

    const bottomY = pageHeight - margin - (footerSections3[0].length * 5);
    footerSections3.forEach((section) => {
      const centerX = pageWidth / 2;
      section.forEach((item, i) => {
        wrapText(item.text, pageWidth - 20).forEach((line, lineIdx) => {
          doc.setFontSize(item.size);
          doc.setFont(item.bold ? 'helvetica' : 'helvetica', item.bold ? 'bold' : 'normal');
          doc.text(line, centerX, bottomY + (i + lineIdx) * 5, { align: 'center' });
        });
      });
    });

    doc.setFontSize(8).setFont('helvetica', 'normal');
    doc.text('Page 1/1', pageWidth - margin, pageHeight - margin, { align: 'right' });

    const baseName = documentType === 'P.O Invoice' || documentType === 'Purchase Order' ? 'POInvoice' : 'CollectionNote';
    const uniqueFilename = generateUniqueFilename(baseName, 'pdf');

    if (viewMode) {
      const blobUrl = doc.output('bloburl');
      window.open(blobUrl, '_blank');
    } else {
      doc.save(uniqueFilename);
    }
  } catch (error) {
    console.error('Error generating PDF:', error);
  }
};

export default generatePurchaseInvoicePDF;
